import { ClosedEndDetails } from './closed-end-details';
import { Leverage, MasterFund, QuantumTag } from '../entities/entities.model';
import { CoInvestmentDetails } from './co-investment-details';
import { SecondaryDetails } from './secondary-details';
import {
  LiquidityStructureEnum,
  VEntityTypeEnum,
  FundStructureEnum,
  SimpleAnswerEnum,
  CloseDateTypeEnum,
  CurrencyEnum,
  ProgramStatusEnum,
  AssetClassEnum,
  FundRaisingStatusEnum,
  HedgeFundStructureEnum,
  ReportingSourceEnum,
  ThirdPartyProviderEnum,
  DataSourceEnum,
  AssetTypeEnum,
  FundStatusEnum,
} from '../../enums';
import {
  StoreModel,
  StoreModelDate,
  StoreModelType,
  UTILS,
} from '@aksia/infrastructure';
import { IInvestmentVehicle } from '../../interfaces/investment-vehicle/investment-vehicle.interface';

export class InvestmentVehicle
  extends StoreModel
  implements IInvestmentVehicle
{
  /**
   * Retrieves the start date of an investment vehicle based on its liquidity structure and various date properties.
   *
   * @param investmentVehicle - The investment vehicle object containing various date properties.
   * @returns The start date of the investment vehicle, or `undefined` if no valid date is found.
   *
   * The function determines the start date as follows:
   * - If the liquidity structure is `OpenEnd`, it returns the `commencementOfOperations` date.
   * - Otherwise, it considers multiple date properties (`firstMgmtFeeAccrualDate`, `firstDrawdownDueDate`, `firstInvestmentDate`, `commencementOfOperations`)
   *   and returns the earliest valid date among them.
   * - If no valid date is found, it falls back to other properties (`vintageYearManager`, `vintageYearThirdParty`, `firstCloseDate`, `finalCloseDate`, `dateOfFormation`)
   *   to determine the start date.
   */
  static readonly getStartDate = (
    investmentVehicle: InvestmentVehicle,
  ): Date | undefined => {
    let streamStartDate;
    if (
      investmentVehicle.liquidityStructure === LiquidityStructureEnum.OpenEnd
    ) {
      streamStartDate = UTILS.DATE.toDate(
        investmentVehicle.commencementOfOperations,
      );
    } else {
      const dates = [
        UTILS.DATE.toDate(investmentVehicle.firstMgmtFeeAccrualDate),
        UTILS.DATE.toDate(investmentVehicle.firstDrawdownDueDate),
        UTILS.DATE.toDate(investmentVehicle.firstInvestmentDate),
        UTILS.DATE.toDate(investmentVehicle.commencementOfOperations),
      ].filter((date) => date);

      if (dates.length > 0) {
        streamStartDate = dates.reduce(
          (a, b) => (a!.getTime() < b!.getTime() ? a : b),
          new Date(),
        );
      }

      if (UTILS.DATE.isDate(streamStartDate)) {
        return streamStartDate;
      }

      streamStartDate = investmentVehicle.vintageYearManager
        ? new Date(investmentVehicle.vintageYearManager!, 0, 1)
        : investmentVehicle.vintageYearThirdParty
          ? new Date(investmentVehicle.vintageYearThirdParty!, 0, 1)
          : investmentVehicle.firstCloseDate
            ? new Date(
                UTILS.DATE.toDate(
                  investmentVehicle.firstCloseDate,
                )!.getFullYear()!,
                0,
                1,
              )
            : investmentVehicle.finalCloseDate
              ? new Date(
                  UTILS.DATE.toDate(
                    investmentVehicle.finalCloseDate,
                  )!.getFullYear()!,
                  0,
                  1,
                )
              : investmentVehicle.dateOfFormation;
    }
    return streamStartDate;
  };

  fundId: number | undefined = undefined;
  name: string | undefined = undefined;
  managementCompanyId: number | undefined = undefined;
  managementCompanyName: string | undefined = undefined;
  investmentProgramId: number | undefined = undefined;
  investmentProgramName: string | undefined = undefined;
  type: AssetTypeEnum | undefined = undefined;
  vintage: number | undefined = undefined;

  //#region <<< Section - Historical Info >>>

  formerNames: string | undefined = undefined;
  nameIsPlaceholder: boolean | undefined = undefined;
  highConviction: boolean | undefined = undefined;
  key: boolean | undefined = undefined;

  //#endregion

  //#region <<< Section - Description >>>

  shortDescription: string | undefined = undefined;

  //#endregion

  //#region <<< Section - Overview >>>

  fundOverview: string | undefined = undefined;

  //#endregion

  //#region <<< Section - Aksia Perspective >>>

  aksiaPerspective: string | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  aksiaPerspectiveAsOf: Date | undefined = undefined;

  //#endregion

  //#region <<< Section - Short Name >>>

  shortName: string | undefined = undefined;

  //#endregion

  //#region <<< Section - AUM >>>

  isAumFirstMonth: boolean | undefined = undefined;
  isAumQuarterly: boolean | undefined = undefined;

  //#endregion

  //#region <<< Section - Taxonomy >>>

  assetClassId: AssetClassEnum | undefined = undefined;
  sectorId: number | undefined = undefined;
  strategyId: number | undefined = undefined;
  substrategyId: number | undefined = undefined;
  primaryRegionId: number | undefined = undefined;

  @StoreModelType(() => QuantumTag)
  quantumTags: Array<QuantumTag> | undefined = undefined;
  esgWomanMinorityDisabledOwnership: number | undefined = undefined;
  womanOwnership: number | undefined = undefined;
  minorityOwnership: number | undefined = undefined;

  //#endregion

  //#region <<< Section - Basic Info >>>

  physicalFileName: string | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  dataAsOfDate: Date | undefined = undefined;

  dataSource: DataSourceEnum | undefined = undefined;

  liquidityStructure: LiquidityStructureEnum | undefined = undefined;
  liquidityType: LiquidityStructureEnum | undefined = undefined;
  vEntityType: VEntityTypeEnum | undefined = undefined;
  legalStructure: FundStructureEnum | undefined = undefined;
  openEndStatus: HedgeFundStructureEnum | undefined = undefined;

  @StoreModelType(() => MasterFund)
  masterFunds: Array<MasterFund> | undefined = undefined;
  mfnProvision: SimpleAnswerEnum | undefined = undefined;
  actsAsMasterFund: boolean | undefined = undefined;

  //#endregion

  //#region <<< Section - Date Info >>>

  @StoreModelType(() => StoreModelDate)
  firmDateEstablished: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  firmInceptionDate: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  investmentProgramDateOfFormation: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  investmentProgramCommencementOfOperations: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  dateOfFormation: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  commencementOfOperations: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  firstMgmtFeeAccrualDate: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  firstDrawdownDueDate: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  firstInvestmentDate: Date | undefined = undefined;
  finalCloseDateType: CloseDateTypeEnum | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  finalCloseDate: Date | undefined = undefined;
  finalCloseDateNote: string | undefined = undefined;
  firstCloseDateType: CloseDateTypeEnum | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  firstCloseDate: Date | undefined = undefined;
  firstCloseDateNote: string | undefined = undefined;
  fundStatus: FundStatusEnum | undefined = undefined;
  calculatedFundStatus: FundStatusEnum | undefined = undefined;
  fundraisingStatus: FundRaisingStatusEnum | undefined = undefined;
  calculatedFundraisingStatus: FundRaisingStatusEnum | undefined = undefined;
  fundraisingNotes: string | undefined = undefined;

  //#endregion

  //#region <<< Reporting >>>

  avgReportingReturns: number | undefined = undefined;
  avgReportingFundAUM: number | undefined = undefined;
  avgReportingExposures: number | undefined = undefined;
  avgReportingIntramonthReturns: number | undefined = undefined;
  firstReportingDelay: boolean | undefined = undefined;
  firstReportingDelayTiming: number | undefined = undefined;
  fundReturnSource: ReportingSourceEnum | undefined = undefined;
  fundAUMSource: ReportingSourceEnum | undefined = undefined;
  logSource: string | undefined = undefined;
  reportingNotes: string | undefined = undefined;

  //#endregion

  //#region <<< Section Leverage >>>

  isLevered: SimpleAnswerEnum | undefined = undefined;
  fundLevelLeverageLimit: SimpleAnswerEnum | undefined = undefined;
  assetLevelLeverageLimit: SimpleAnswerEnum | undefined = undefined;

  @StoreModelType(() => Leverage)
  leverage: Array<Leverage> | undefined = undefined;

  //#endregion

  //#region <<< Section - Pacing >>>

  cashInterest: number | undefined = undefined;
  contributionRates: string | undefined = undefined;
  distributionRates: string | undefined = undefined;
  growthRates: string | undefined = undefined;

  //#endregion

  //#region <<< Section - Key Terms >>>

  vintageYearManager: number | undefined = undefined;
  vintageYearThirdParty: number | undefined = undefined;
  thirdPartyProvider: ThirdPartyProviderEnum | undefined = undefined;

  //#endregion

  //#region <<< Section - Key Considerations >>>

  excludeForwardCalendar: boolean | undefined = undefined;

  //#endregion

  //#region <<< Section - Identification >>>

  fundCRD: number | undefined = undefined;
  fundCIK: number | undefined = undefined;
  advIdentifier: number | undefined = undefined;

  //#endregion

  commitmentCurrency: CurrencyEnum | undefined = undefined;
  programStatus: ProgramStatusEnum | undefined = undefined;
  closedEndDetails: ClosedEndDetails = new ClosedEndDetails();
  coInvestmentDetails: CoInvestmentDetails = new CoInvestmentDetails();
  secondaryDetails: SecondaryDetails = new SecondaryDetails();
}
